import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@hero/web-lib';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/DropdownMenu/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/Tooltip/Tooltip';
import { Spinner } from '../Spinner';

/** Trailing control on the end edge of a row (LTR right / RTL left via `ms-auto`). */
export interface ActionButtonTrailingAction {
  icon: React.ReactNode;
  /** Accessible name for the icon button. */
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** Native tooltip / title. */
  title?: string;
}

export interface ActionButtonOption {
  id: string;
  label: string;
  description?: string;
  disabledDescription?: string; // Shown when disabled instead of description
  icon?: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  highlight?: boolean;
  hide?: boolean; // Hide the option completely
  onClick: () => void;
  /** Optional end-edge action (e.g. redo). Independent disabled/loading/onClick. */
  trailingAction?: ActionButtonTrailingAction;
}

export interface ActionButtonGroup {
  id: string;
  label: string;
  description?: string;
  disabledDescription?: string; // Shown when disabled instead of description
  icon?: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  highlight?: boolean;
  hide?: boolean; // Hide the option completely
  onClick?: () => void;
  groupOptions?: ActionButtonOption[];
  /** Optional end-edge action (e.g. redo). Independent disabled/loading/onClick. */
  trailingAction?: ActionButtonTrailingAction;
}

export interface ActionButtonProps {
  actions: ActionButtonGroup[];
  buttonLabel?: string;
  buttonIcon?: React.ReactNode;
  className?: string;
  buttonClassName?: string;
}

function ActionRowContent({
  label,
  description,
  icon,
  disabled,
  highlight,
  isLoading,
  trailingAction,
  onTrailingClick,
}: {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  highlight?: boolean;
  isLoading?: boolean;
  trailingAction?: ActionButtonTrailingAction;
  onTrailingClick: (trailing: ActionButtonTrailingAction) => void;
}) {
  return (
    <div className="flex items-start gap-3 w-full">
      {icon ? <div className="flex-shrink-0 mt-0.5 text-gray-500">{icon}</div> : null}
      <div className="flex-1 flex flex-col gap-1 min-w-0">
        <span
          className={cn(
            'font-medium text-sm',
            highlight && 'text-blue-600',
            disabled && 'text-gray-400',
          )}>
          {label}
        </span>
        {description ? <span className="text-xs text-gray-500">{description}</span> : null}
      </div>
      {/* End-edge control: `ms-auto` keeps it on the inline-end in LTR and RTL. */}
      {isLoading ? (
        <div className="flex-shrink-0 ms-auto mt-0.5">
          <Spinner />
        </div>
      ) : trailingAction ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Span wrapper so the tooltip still works when the button is disabled. */}
              <span className="flex-shrink-0 ms-auto mt-0.5 inline-flex">
                <button
                  type="button"
                  aria-label={trailingAction.ariaLabel}
                  disabled={trailingAction.disabled || trailingAction.isLoading}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (trailingAction.disabled || trailingAction.isLoading) return;
                    onTrailingClick(trailingAction);
                  }}
                  className={cn(
                    'inline-flex items-center justify-center',
                    'rounded-md p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100',
                    'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500',
                  )}>
                  {trailingAction.isLoading ? (
                    <Spinner className="size-4" />
                  ) : (
                    trailingAction.icon
                  )}
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {trailingAction.title ?? trailingAction.ariaLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  actions,
  buttonLabel = 'Actions',
  buttonIcon,
  className,
  buttonClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const visibleActions = actions.filter(action => !action.hide);

  const runMainAction = (action: ActionButtonGroup | ActionButtonOption) => {
    if (action.disabled || action.isLoading || !action.onClick) return;
    setIsOpen(false);
    action.onClick();
  };

  const runTrailing = (trailing: ActionButtonTrailingAction) => {
    setIsOpen(false);
    trailing.onClick();
  };

  return (
    <div className={cn('relative w-full', className)}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex items-center justify-center gap-2 rounded-[32px] w-full text-[16px] font-[500] leading-[26px] px-6 py-3',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'transition-colors duration-200',
              'focus:outline-none focus-visible:outline-none',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              buttonClassName,
            )}>
            {buttonIcon}
            <span>{buttonLabel}</span>
            <ChevronDown
              className={cn('w-4 h-4 transition-transform duration-200', isOpen && 'rotate-180')}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="center"
          className="w-[400px] max-w-[calc(100vw-2rem)]">
          {visibleActions.map((action, actionIndex) => {
            // If has groupOptions, render as section with nested options
            if (action.groupOptions && action.groupOptions.length > 0) {
              return (
                <React.Fragment key={action.id}>
                  <DropdownMenuLabel className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                    {action.label}
                  </DropdownMenuLabel>
                  {action.groupOptions
                    .filter(option => !option.hide)
                    .map(option => {
                      const displayDescription =
                        option.disabled && option.disabledDescription
                          ? option.disabledDescription
                          : option.description;
                      // Keep the row interactive when a trailing control can still run.
                      const rowDisabled =
                        !option.trailingAction && (option.disabled || option.isLoading);

                      return (
                        <DropdownMenuItem
                          key={option.id}
                          disabled={rowDisabled}
                          onClick={e => {
                            e.preventDefault();
                            runMainAction(option);
                          }}
                          className={cn(
                            'px-4 py-3 cursor-pointer',
                            option.highlight && 'font-bold text-blue-600 bg-blue-50/50',
                            option.isLoading && 'opacity-75 cursor-wait',
                            option.disabled && option.trailingAction && 'cursor-default',
                          )}>
                          <ActionRowContent
                            label={option.label}
                            description={displayDescription}
                            icon={option.icon}
                            disabled={option.disabled}
                            highlight={option.highlight}
                            isLoading={option.isLoading}
                            trailingAction={option.trailingAction}
                            onTrailingClick={runTrailing}
                          />
                        </DropdownMenuItem>
                      );
                    })}
                  {actionIndex < visibleActions.length - 1 && <DropdownMenuSeparator />}
                </React.Fragment>
              );
            }

            const displayDescription =
              action.disabled && action.disabledDescription
                ? action.disabledDescription
                : action.description;
            const rowDisabled =
              !action.trailingAction && (action.disabled || action.isLoading);

            return (
              <React.Fragment key={action.id}>
                <DropdownMenuItem
                  disabled={rowDisabled}
                  onClick={e => {
                    e.preventDefault();
                    runMainAction(action);
                  }}
                  className={cn(
                    'px-4 py-3 cursor-pointer',
                    action.highlight && 'font-bold text-blue-600 bg-blue-50/50',
                    action.isLoading && 'opacity-75 cursor-wait',
                    action.disabled && action.trailingAction && 'cursor-default',
                  )}>
                  <ActionRowContent
                    label={action.label}
                    description={displayDescription}
                    icon={action.icon}
                    disabled={action.disabled}
                    highlight={action.highlight}
                    isLoading={action.isLoading}
                    trailingAction={action.trailingAction}
                    onTrailingClick={runTrailing}
                  />
                </DropdownMenuItem>
                {actionIndex < visibleActions.length - 1 && <DropdownMenuSeparator />}
              </React.Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
